<template>
  <div class="text-center">
    <v-dialog
      v-model="$store.rdiStore.state.weeklyDashboardModal"
      max-width="960"
      scrollable
    >
      <v-card>
        <v-card-text class="pa-0">
          <div class="py-4 px-6">
            <div class="d-flex flex-row align-center justify-space-between mb-4">
              <div class="d-flex flex-row align-center">
                <v-icon color="white" small class="info-icon mr-2">mdi-chart-bar</v-icon>
                <p class="mb-0 font-weight-bold text-primary title">Dashboard Semanal de RDI</p>
              </div>
              <v-btn class="mx-2" fab dark small color="#F4516C" @click="closeModal">
                <v-icon dark>mdi-close</v-icon>
              </v-btn>
            </div>

            <div class="weekly-toolbar mb-3" :class="{'is-mobile': isSmallScreen}">
              <p class="mb-0 subtitle-2 text-primary-color">Paginación de 4 semanas (corte al viernes)</p>
              <div class="weekly-toolbar-actions">
                <v-btn
                  rounded
                  small
                  class="mr-2 mb-2"
                  :disabled="loading || page <= 1"
                  @click="goToPage(page - 1)"
                >
                  <v-icon small>mdi-chevron-left</v-icon>
                  <span class="ml-1">Anterior</span>
                </v-btn>
                <span class="caption mr-2 mb-2">Página {{ page }} / {{ totalPages || 1 }}</span>
                <v-btn
                  rounded
                  small
                  class="mr-3 mb-2"
                  :disabled="loading || !hasMore"
                  @click="goToPage(page + 1)"
                >
                  <span class="mr-1">Siguiente</span>
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn
                  rounded
                  small
                  color="#AAAAAA"
                  class="mb-2 weekly-export-btn"
                  :disabled="loadingExport || loading"
                  @click="downloadExcel"
                >
                  <div class="d-flex align-center" v-if="loadingExport">
                    <v-progress-circular indeterminate color="white" :width="2" :size="15"></v-progress-circular>
                    <p class="mb-0 ml-2 white--text">Preparando Excel</p>
                  </div>
                  <div class="d-flex align-center" v-else>
                    <v-icon color="white">mdi-microsoft-excel</v-icon>
                    <p class="mb-0 ml-2 white--text">Exportar</p>
                  </div>
                </v-btn>
              </div>
            </div>

            <v-progress-linear
              v-if="loading"
              indeterminate
              color="#5e85ef"
              class="mb-4"
            ></v-progress-linear>

            <div class="weekly-chart-wrapper" v-if="chartData.labels.length > 0 && !isSmallScreen">
              <div class="weekly-chart-inner">
                <BarChart
                  :chartdata="chartData"
                  :options="chartOptions"
                />
              </div>
            </div>
            <p v-else-if="chartData.labels.length > 0" class="caption text-primary-color mb-2">
              Gráfico oculto en pantalla pequeña para priorizar legibilidad.
            </p>

            <div class="weekly-table-wrapper mt-2">
              <v-simple-table class="weekly-table">
                <thead>
                  <tr>
                    <th class="text-left">Semana</th>
                    <th class="text-left">Inicio</th>
                    <th class="text-left">Fin</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Finalizadas</th>
                    <th class="text-right">Pendientes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(record, index) in records" :key="index">
                    <td>{{ record.label }}</td>
                    <td>{{ record.week_start }}</td>
                    <td>{{ record.week_end }}</td>
                    <td class="text-right">{{ record.total_rdis }}</td>
                    <td class="text-right">{{ record.ended_rdis }}</td>
                    <td class="text-right">{{ record.pending_rdis }}</td>
                  </tr>
                  <tr v-if="!loading && records.length === 0">
                    <td colspan="6" class="text-center">Sin datos para este rango</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/Charts/Bar'
import {getDashboardRDIWeekly, getDashboardRDIWeeklyExport} from '@/helpers/api/dashboard'

export default {
	name: 'DashboardRDIWeeklyModal',
  components: {BarChart},
  data: () => ({
    loading: false,
    loadingExport: false,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    page: 1,
    totalPages: 0,
    hasMore: false,
    records: []
  }),
  computed: {
    isSmallScreen(){
      return this.windowWidth < 900
    },
    chartData(){
      return {
        labels: this.records.map((record) => record.label),
        datasets: [
          {
            label: 'Total',
            backgroundColor: '#EAB261',
            data: this.records.map((record) => record.total_rdis)
          },
          {
            label: 'Finalizadas',
            backgroundColor: '#34BFA3',
            data: this.records.map((record) => record.ended_rdis)
          },
          {
            label: 'Pendientes',
            backgroundColor: '#F45771',
            data: this.records.map((record) => record.pending_rdis)
          }
        ]
      }
    },
    chartOptions(){
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          position: 'bottom'
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
              return `${datasetLabel}: ${tooltipItem.yLabel}`
            }
          }
        },
        scales: {
          xAxes: [{
            ticks: {
              autoSkip: true,
              maxTicksLimit: 4,
              maxRotation: 0,
              minRotation: 0
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            }
          }]
        }
      }
    }
  },
  mounted(){
    window.addEventListener('resize', this.onResize)
    this.fetchWeeklyData()
  },
  beforeDestroy(){
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize(){
      this.windowWidth = window.innerWidth
    },
    closeModal(){
      this.$store.rdiStore.commit('setWeeklyDashboardModal', false)
    },
    getProjectId(){
      if(this.$store.projectsStore.state.selectedProject){
        return this.$store.projectsStore.state.selectedProject.id
      }
      const stored = localStorage.getItem('selectedProjectID')
      return stored ? parseInt(stored) : null
    },
    async fetchWeeklyData(){
      const project_id = this.getProjectId()
      if(!project_id){
        return
      }

      this.loading = true
      const response = await getDashboardRDIWeekly({project_id, page: this.page})
      if(response.code === 200){
        this.records = response.records || []
        this.hasMore = !!response.has_more
        this.totalPages = response.total_pages || 0
      }
      this.loading = false
    },
    async goToPage(page){
      this.page = page
      await this.fetchWeeklyData()
    },
    async downloadExcel(){
      const project_id = this.getProjectId()
      if(!project_id){
        return
      }

      this.loadingExport = true
      const response = await getDashboardRDIWeeklyExport({project_id, page: this.page})
      if(response.code === 200){
        window.open(response.url)
      }
      this.loadingExport = false
    }
  }
}
</script>

<style scoped>
.text-primary { color: #2D4B95; }
.text-primary-color { color: #2D4B95; }
.weekly-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
}
.weekly-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.weekly-chart-wrapper {
  height: 300px;
  margin-bottom: 28px;
  overflow: hidden;
}
.weekly-chart-inner {
  height: 100%;
}
/deep/ .weekly-chart-inner canvas {
  max-height: 100% !important;
  display: block !important;
}
.weekly-table-wrapper {
  overflow-x: auto;
}
.weekly-table {
  min-width: 680px;
}

@media (max-width: 600px) {
  .weekly-toolbar.is-mobile {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .weekly-toolbar-actions {
    width: 100%;
  }
  .weekly-export-btn {
    margin-left: 0 !important;
  }
  .weekly-chart-wrapper {
    height: 260px;
    margin-bottom: 20px;
  }
}
</style>
